import { useEffect, useMemo, useRef, useState } from "react";
import {
  App as AntdApp,
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Result,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  ImportOutlined,
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { RcFile } from "antd/es/upload/interface";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { contactImportService } from "../../../services/api/contactImportService";
import { usePageTitle } from "../../../hooks";
import { PageHeader } from "../../../components/common/PageHeader";
import type {
  ImportApplyResult,
  ImportPreviewResponse,
  ImportRow,
  ImportRowStatus,
  ParsedContact,
} from "../../../types/contact-import";

interface ProducerOption {
  id: number;
  name: string;
}

interface FarmOption {
  id: number;
  name: string;
  producer: number;
}

type ContactKind = "farm" | "license";

type EditingState = {
  rowIndex: number;
  kind: ContactKind;
} | null;

interface ContactFormValues {
  name?: string;
  surname?: string;
  main_phone?: string;
  secondary_phone?: string;
  email?: string;
}

const STATUS_ORDER: Record<ImportRowStatus, number> = { ok: 0, warning: 1, error: 2 };

const ALL_MESSAGE_CODES = [
  "missing_audit_code",
  "audit_code_not_found",
  "farm_name_mismatch",
  "farm_contact_composite_name",
  "license_contact_composite_name",
  "no_contacts_in_row",
  "license_conflict_first_kept",
  "farm_contact_already_linked",
  "license_contact_already_linked",
];

const contactSortKey = (c: ParsedContact | null): string =>
  c ? `${c.name} ${c.surname}`.trim().toLowerCase() : "";

const contactSearchHaystack = (c: ParsedContact | null): string =>
  c ? [c.name, c.surname, c.main_phone, c.secondary_phone, c.email].join(" ").toLowerCase() : "";

export const ContactImportPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message: notify } = AntdApp.useApp();

  usePageTitle(t("contact_import.title"));

  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [searchText, setSearchText] = useState("");
  const [editing, setEditing] = useState<EditingState>(null);
  const [form] = Form.useForm<ContactFormValues>();

  // Lazy-load producers + farms only when there's at least one error row
  // requiring an override; otherwise we don't need them.
  const needsOverride = rows.some((r) => r.status === "error" && !r.producer_id);

  // Server-side producer search. The list endpoint paginates at max 100 rows,
  // so a "fetch all and filter client-side" approach would silently drop
  // producers past the first page. We hit `?search=...` instead.
  const [producerSearch, setProducerSearch] = useState("");
  const [producers, setProducers] = useState<ProducerOption[]>([]);
  const [loadingProducers, setLoadingProducers] = useState(false);
  // Names of producers the user already picked in some row — kept around so
  // the Select can render their label even after the search query changes.
  const [pickedProducerNames, setPickedProducerNames] = useState<Record<number, string>>({});
  const producerSearchTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!needsOverride) return;
    if (producerSearchTimer.current) {
      window.clearTimeout(producerSearchTimer.current);
    }
    producerSearchTimer.current = window.setTimeout(async () => {
      setLoadingProducers(true);
      try {
        const r = await apiClient.get("/v1/producers/", {
          params: { search: producerSearch || undefined, page_size: 50 },
        });
        const list: ProducerOption[] =
          r.data?.data ?? r.data?.results ?? r.data ?? [];
        setProducers(list);
      } catch {
        setProducers([]);
      } finally {
        setLoadingProducers(false);
      }
    }, 250);
    return () => {
      if (producerSearchTimer.current) window.clearTimeout(producerSearchTimer.current);
    };
  }, [producerSearch, needsOverride]);

  // Farm selector for the override dropdown — fetched per producer override
  // (we cache per producer id).
  const [farmsByProducer, setFarmsByProducer] = useState<Record<number, FarmOption[]>>({});
  const fetchFarmsFor = async (producerId: number) => {
    if (farmsByProducer[producerId]) return;
    try {
      const r = await apiClient.get(
        `/v1/farms/`, { params: { producer: producerId, page_size: 100 } },
      );
      const list: FarmOption[] = r.data?.data ?? r.data ?? [];
      setFarmsByProducer((prev) => ({ ...prev, [producerId]: list }));
    } catch {
      setFarmsByProducer((prev) => ({ ...prev, [producerId]: [] }));
    }
  };

  const upload = async (file: RcFile) => {
    setParsing(true);
    setPreview(null);
    setResult(null);
    setSearchText("");
    try {
      const data = await contactImportService.preview(file);
      setPreview(data);
      setRows(data.rows);
      setSelected(new Set(data.rows.filter((r) => r.status !== "error").map((r) => r.row_index)));
    } catch (e: any) {
      const detail =
        (typeof e?.response?.data === "object" && e.response.data?.detail) ||
        t("contact_import.parse_error");
      notify.error(String(detail));
    } finally {
      setParsing(false);
    }
    return false;
  };

  const onApply = async () => {
    const toApply = rows
      .filter((r) => selected.has(r.row_index))
      .map((r) => ({
        ...r,
        producer_id: r.producer_id_override ?? r.producer_id,
      }));
    setApplying(true);
    try {
      const summary = await contactImportService.apply(toApply);
      setResult(summary);
      notify.success(t("contact_import.apply_success"));
    } catch {
      notify.error(t("contact_import.apply_error"));
    } finally {
      setApplying(false);
    }
  };

  const setOverrideProducer = (rowIndex: number, producerId: number | null) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row_index === rowIndex
          ? { ...r, producer_id_override: producerId, farm_id: null }
          : r,
      ),
    );
    if (producerId) {
      const picked = producers.find((p) => p.id === producerId);
      if (picked) {
        setPickedProducerNames((prev) =>
          prev[producerId] ? prev : { ...prev, [producerId]: picked.name },
        );
      }
      void fetchFarmsFor(producerId);
    }
  };

  const setOverrideFarm = (rowIndex: number, farmId: number | null) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row_index === rowIndex ? { ...r, farm_id: farmId } : r,
      ),
    );
  };

  // ----- contact edit modal ------------------------------------------------

  const openEdit = (rowIndex: number, kind: ContactKind) => {
    setEditing({ rowIndex, kind });
  };

  // Computed once per modal-open so the Form's initialValues prop receives
  // the right snapshot. Setting values via form.setFieldsValue before
  // setEditing doesn't work because the modal uses destroyOnClose — the form
  // instance is unmounted when the modal is closed and the values are lost.
  const editingInitialValues: ContactFormValues = useMemo(() => {
    if (!editing) return {};
    const row = rows.find((r) => r.row_index === editing.rowIndex);
    const existing = !row
      ? null
      : editing.kind === "farm"
        ? row.farm_contact
        : row.license_contact;
    return {
      name: existing?.name ?? "",
      surname: existing?.surname ?? "",
      main_phone: existing?.main_phone ?? "",
      secondary_phone: existing?.secondary_phone ?? "",
      email: existing?.email ?? "",
    };
    // We intentionally don't depend on `rows` — the modal's lifetime is
    // tied to `editing`, and rows are mutated by saveEdit which closes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const closeEdit = () => {
    setEditing(null);
    form.resetFields();
  };

  const saveEdit = async () => {
    if (!editing) return;
    const values = await form.validateFields();
    const trimmed = {
      name: (values.name ?? "").trim(),
      surname: (values.surname ?? "").trim(),
      main_phone: (values.main_phone ?? "").trim(),
      secondary_phone: (values.secondary_phone ?? "").trim(),
      email: (values.email ?? "").trim(),
    };
    const isEmpty =
      !trimmed.name && !trimmed.surname && !trimmed.main_phone && !trimmed.email;

    setRows((prev) =>
      prev.map((r) => {
        if (r.row_index !== editing.rowIndex) return r;
        const newContact: ParsedContact | null = isEmpty
          ? null
          : {
              raw_name: `${trimmed.name} ${trimmed.surname}`.trim(),
              name: trimmed.name,
              surname: trimmed.surname,
              main_phone: trimmed.main_phone,
              secondary_phone: trimmed.secondary_phone,
              email: trimmed.email,
              warnings: [],
            };
        return editing.kind === "farm"
          ? { ...r, farm_contact: newContact }
          : { ...r, license_contact: newContact };
      }),
    );
    closeEdit();
  };

  const clearContactInModal = () => {
    form.setFieldsValue({
      name: "",
      surname: "",
      main_phone: "",
      secondary_phone: "",
      email: "",
    });
  };

  // ----- search + filtering -----------------------------------------------

  const displayedRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const haystack = [
        r.audit_code,
        r.producer_name_in_db ?? "",
        r.producer_name_in_xlsx,
        r.farm_name_in_db ?? "",
        r.farm_name_in_xlsx,
        contactSearchHaystack(r.farm_contact),
        contactSearchHaystack(r.license_contact),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchText]);

  const canApply = useMemo(() => {
    if (selected.size === 0) return false;
    return rows.every((r) => {
      if (!selected.has(r.row_index)) return true;
      const pid = r.producer_id_override ?? r.producer_id;
      if (!pid) return false;
      return true;
    });
  }, [rows, selected]);

  // Keep the ref to rows in sync when the editing modal is open and rows change
  // (e.g. user toggles selection/override without closing the modal).
  useEffect(() => {
    if (!editing) return;
    const stillThere = rows.find((r) => r.row_index === editing.rowIndex);
    if (!stillThere) closeEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, editing]);

  if (result) {
    // Group errors by reason to give the admin a one-glance idea of
    // what went wrong (typically dominated by a single root cause).
    const errorGroups = result.errors.reduce<Record<string, number>>(
      (acc, e) => {
        const key = (e.error || "unknown").trim();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {},
    );
    const errorGroupsSorted = Object.entries(errorGroups).sort(
      (a, b) => b[1] - a[1],
    );

    return (
      <Card>
        <PageHeader
          icon={<FileExcelOutlined />}
          accent="#52C41A"
          title={t("contact_import.title")}
          subtitle={t("contact_import.apply_success")}
          extra={
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/producers")}
              style={{ borderRadius: 999, paddingInline: 16 }}
            >
              {t("contact_chains.back_to_producer")}
            </Button>
          }
        />
        <Result
          status="success"
          title={t("contact_import.apply_success")}
          extra={[
            <Row gutter={16} key="stats" style={{ marginTop: 24 }}>
              <Col xs={12} sm={8}>
                <Statistic title={t("contact_import.result_rows")} value={result.rows_processed} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title={t("contact_import.result_contacts_created")} value={result.contacts_created} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title={t("contact_import.result_contacts_reused")} value={result.contacts_reused} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title={t("contact_import.result_roles_created")} value={result.roles_created} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title={t("contact_import.result_roles_skipped")} value={result.roles_skipped} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title={t("contact_import.result_errors")}
                  value={result.errors.length}
                  valueStyle={{ color: result.errors.length ? "#cf1322" : undefined }}
                />
              </Col>
            </Row>,
          ]}
        />
        {result.errors.length > 0 && (
          <Card
            type="inner"
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#cf1322" }} />
                {t(
                  "contact_import.errors_title",
                  "Detalle de errores ({{n}})",
                  { n: result.errors.length },
                )}
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(
                "contact_import.errors_grouped",
                "Resumen por motivo",
              )}
              description={
                <Space size={[8, 8]} wrap>
                  {errorGroupsSorted.map(([reason, count]) => (
                    <Tag key={reason} color="red">
                      {reason}: {count}
                    </Tag>
                  ))}
                </Space>
              }
            />
            <Table
              dataSource={result.errors}
              rowKey={(_e, idx) => String(idx)}
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              columns={[
                {
                  title: t("contact_import.col_row", "Fila"),
                  dataIndex: "row",
                  width: 80,
                },
                {
                  title: t("contact_import.col_audit_code", "Cód. Auditoría"),
                  dataIndex: "audit_code",
                  width: 160,
                  render: (v: string) => v || "—",
                },
                {
                  title: t("contact_import.col_producer", "Productor"),
                  dataIndex: "producer",
                  render: (v: string) => v || "—",
                },
                {
                  title: t("contact_import.col_error", "Motivo"),
                  dataIndex: "error",
                  render: (v: string) => <Tag color="red">{v}</Tag>,
                },
              ]}
            />
          </Card>
        )}
      </Card>
    );
  }

  const editingRow = editing
    ? rows.find((r) => r.row_index === editing.rowIndex) ?? null
    : null;
  const editingTitle = editing
    ? editing.kind === "farm"
      ? t(
          editingRow?.farm_contact ? "contact_import.edit_farm_contact" : "contact_import.add_farm_contact",
        )
      : t(
          editingRow?.license_contact ? "contact_import.edit_license_contact" : "contact_import.add_license_contact",
        )
    : "";

  return (
    <Card>
      <PageHeader
        icon={<FileExcelOutlined />}
        accent="#1677FF"
        title={t("contact_import.title")}
        subtitle={t("contact_import.subtitle")}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/producers")}
            style={{ borderRadius: 999, paddingInline: 16 }}
          >
            {t("contact_chains.back_to_producer")}
          </Button>
        }
      />

      {!preview && (
        <Upload.Dragger
          accept=".xlsx"
          multiple={false}
          showUploadList={false}
          beforeUpload={upload}
          disabled={parsing}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ fontSize: 48, color: "#1677FF" }} />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 16 }}>
            {t("contact_import.upload_help")}
          </p>
          <Button
            type="primary"
            loading={parsing}
            style={{ marginTop: 8 }}
            icon={<FileExcelOutlined />}
          >
            {t("contact_import.upload_button")}
          </Button>
        </Upload.Dragger>
      )}

      {preview && (
        <>
          <Row gutter={16} style={{ marginTop: 4, marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Statistic title={t("contact_import.summary_total")} value={preview.summary.total} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t("contact_import.summary_ok")}
                value={preview.summary.ok}
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t("contact_import.summary_warning")}
                value={preview.summary.warning}
                valueStyle={{ color: "#faad14" }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t("contact_import.summary_error")}
                value={preview.summary.error}
                valueStyle={{ color: "#cf1322" }}
              />
            </Col>
          </Row>

          {needsOverride && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t("contact_import.msg_audit_code_not_found")}
              description={t("contact_import.override_producer") + " — " + t("contact_import.override_farm")}
            />
          )}

          <Space style={{ marginBottom: 12 }} wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t("contact_import.search_placeholder")}
              style={{ width: 360 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Space>

          <Table<ImportRow>
            dataSource={displayedRows}
            rowKey="row_index"
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: 1300 }}
            rowSelection={{
              selectedRowKeys: Array.from(selected),
              onChange: (keys) => setSelected(new Set(keys.map(Number))),
              getCheckboxProps: () => ({ disabled: false }),
            }}
            columns={[
              {
                title: t("contact_import.table_row"),
                dataIndex: "row_index",
                key: "row_index",
                width: 70,
                sorter: (a, b) => a.row_index - b.row_index,
                defaultSortOrder: "ascend",
              },
              {
                title: t("contact_import.table_status"),
                dataIndex: "status",
                key: "status",
                width: 110,
                sorter: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
                filters: [
                  { text: t("contact_import.ok_label"), value: "ok" },
                  { text: t("contact_import.warning_label"), value: "warning" },
                  { text: t("contact_import.error_label"), value: "error" },
                ],
                onFilter: (value, record) => record.status === value,
                render: (s: string) => <StatusTag status={s as ImportRowStatus} t={t} />,
              },
              {
                title: t("contact_import.table_audit_code"),
                dataIndex: "audit_code",
                key: "audit_code",
                width: 130,
                sorter: (a, b) => (a.audit_code || "").localeCompare(b.audit_code || ""),
                render: (v: string) => v || "—",
              },
              {
                title: t("contact_import.table_producer"),
                key: "producer",
                width: 240,
                sorter: (a, b) => {
                  const an = (a.producer_name_in_db || a.producer_name_in_xlsx || "").toLowerCase();
                  const bn = (b.producer_name_in_db || b.producer_name_in_xlsx || "").toLowerCase();
                  return an.localeCompare(bn);
                },
                render: (_: unknown, row: ImportRow) => {
                  if (row.producer_id) return <span>{row.producer_name_in_db}</span>;
                  // Merge search results with previously-picked producers so
                  // the current value always renders, even when the user
                  // searches for something else afterwards.
                  const optionMap = new Map<number, string>();
                  Object.entries(pickedProducerNames).forEach(([id, name]) => {
                    optionMap.set(Number(id), name);
                  });
                  producers.forEach((p) => optionMap.set(p.id, p.name));
                  return (
                    <Select
                      placeholder={t("contact_import.select_producer")}
                      showSearch
                      filterOption={false}
                      onSearch={(value) => setProducerSearch(value)}
                      loading={loadingProducers}
                      style={{ width: "100%", minWidth: 180 }}
                      value={row.producer_id_override ?? undefined}
                      onChange={(v) => setOverrideProducer(row.row_index, v ?? null)}
                      options={Array.from(optionMap.entries()).map(([id, name]) => ({
                        value: id,
                        label: name,
                      }))}
                      allowClear
                      notFoundContent={loadingProducers ? t("common.loading") : null}
                    />
                  );
                },
              },
              {
                title: t("contact_import.table_farm"),
                key: "farm",
                width: 200,
                sorter: (a, b) => {
                  const an = (a.farm_name_in_db || a.farm_name_in_xlsx || "").toLowerCase();
                  const bn = (b.farm_name_in_db || b.farm_name_in_xlsx || "").toLowerCase();
                  return an.localeCompare(bn);
                },
                render: (_: unknown, row: ImportRow) => {
                  if (row.farm_id && row.farm_name_in_db) return row.farm_name_in_db;
                  const pid = row.producer_id_override ?? row.producer_id ?? null;
                  if (!pid) return <span style={{ color: "#bfbfbf" }}>—</span>;
                  const list = farmsByProducer[pid] ?? [];
                  if (list.length === 0) {
                    void fetchFarmsFor(pid);
                  }
                  return (
                    <Select
                      placeholder={t("contact_import.select_farm")}
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%", minWidth: 160 }}
                      value={row.farm_id ?? undefined}
                      onChange={(v) => setOverrideFarm(row.row_index, v ?? null)}
                      options={list.map((f) => ({ value: f.id, label: f.name }))}
                      allowClear
                    />
                  );
                },
              },
              {
                title: t("contact_import.table_farm_contact"),
                key: "farm_contact",
                sorter: (a, b) => contactSortKey(a.farm_contact).localeCompare(contactSortKey(b.farm_contact)),
                render: (_: unknown, row: ImportRow) => (
                  <ContactCellEditable
                    contact={row.farm_contact}
                    onEdit={() => openEdit(row.row_index, "farm")}
                    addLabel={t("contact_import.add_farm_contact")}
                  />
                ),
              },
              {
                title: t("contact_import.table_license_contact"),
                key: "license_contact",
                sorter: (a, b) => contactSortKey(a.license_contact).localeCompare(contactSortKey(b.license_contact)),
                render: (_: unknown, row: ImportRow) => (
                  <ContactCellEditable
                    contact={row.license_contact}
                    showEmail
                    onEdit={() => openEdit(row.row_index, "license")}
                    addLabel={t("contact_import.add_license_contact")}
                  />
                ),
              },
              {
                title: t("contact_import.table_messages"),
                key: "messages",
                filters: ALL_MESSAGE_CODES.map((code) => ({
                  text: t(`contact_import.msg_${code}`, code),
                  value: code,
                })),
                onFilter: (value, record) => record.messages.includes(String(value)),
                render: (_: unknown, row: ImportRow) => (
                  <Space size={[4, 4]} wrap>
                    {row.messages.map((m) => (
                      <Tooltip key={m} title={t(`contact_import.msg_${m}`, m)}>
                        <Tag color={row.status === "error" ? "red" : "orange"}>
                          {t(`contact_import.msg_${m}`, m)}
                        </Tag>
                      </Tooltip>
                    ))}
                  </Space>
                ),
              },
            ]}
          />

          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              icon={<ImportOutlined />}
              onClick={onApply}
              loading={applying}
              disabled={!canApply}
            >
              {t("contact_import.btn_apply", { count: selected.size })}
            </Button>
          </div>
        </>
      )}

      <Modal
        open={editing !== null}
        title={editingTitle}
        onCancel={closeEdit}
        onOk={saveEdit}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        destroyOnClose
        footer={[
          <Button key="clear" danger icon={<DeleteOutlined />} onClick={clearContactInModal}>
            {t("contact_import.clear_contact")}
          </Button>,
          <Button key="cancel" onClick={closeEdit}>
            {t("common.cancel")}
          </Button>,
          <Button key="save" type="primary" onClick={saveEdit}>
            {t("common.save")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={editingInitialValues}
        >
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item name="name" label={t("contact_import.field_name")}>
                <Input autoFocus />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="surname" label={t("contact_import.field_surname")}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item name="main_phone" label={t("contact_import.field_main_phone")}>
                <Input />
              </Form.Item>
            </Col>
            {editing?.kind === "license" && (
              <Col xs={24} sm={12}>
                <Form.Item name="secondary_phone" label={t("contact_import.field_secondary_phone")}>
                  <Input />
                </Form.Item>
              </Col>
            )}
          </Row>
          {editing?.kind === "license" && (
            <Form.Item
              name="email"
              label={t("contact_import.field_email")}
              rules={[{ type: "email", message: t("common.error") }]}
            >
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

const ContactCellEditable = ({
  contact,
  showEmail,
  onEdit,
  addLabel,
}: {
  contact: ParsedContact | null;
  showEmail?: boolean;
  onEdit: () => void;
  addLabel: string;
}) => {
  if (!contact) {
    return (
      <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={onEdit}>
        {addLabel}
      </Button>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <div style={{ lineHeight: 1.3, flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>
          {`${contact.name} ${contact.surname}`.trim() || "—"}
        </div>
        {contact.main_phone && (
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>{contact.main_phone}</div>
        )}
        {showEmail && contact.email && (
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>{contact.email}</div>
        )}
      </div>
      <Button
        size="small"
        type="text"
        icon={<EditOutlined />}
        onClick={onEdit}
      />
    </div>
  );
};

const StatusTag = ({
  status,
  t,
}: {
  status: ImportRowStatus;
  t: (k: string) => string;
}) => {
  if (status === "ok") {
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        {t("contact_import.ok_label")}
      </Tag>
    );
  }
  if (status === "warning") {
    return (
      <Tag color="orange" icon={<ExclamationCircleOutlined />}>
        {t("contact_import.warning_label")}
      </Tag>
    );
  }
  return (
    <Tag color="red" icon={<CloseCircleOutlined />}>
      {t("contact_import.error_label")}
    </Tag>
  );
};

export default ContactImportPage;
